import { Feature } from "ol";
import { FeatureType } from "@/geoMap/define/FeatureType";
import { AttributeValue } from "./AttributeValue";
import { AttributeView } from "./AttributeView";
import { IAttributeConvert } from "./IAttributeConvert";
import {
  CarType,
  ODDataType,
  ODYearType,
} from "@/geoMap/layerFactory/IODLayerName";
import { ExportValue } from "./ExportValue";

/**
 * 画面サイズ（横幅）
 */
const VALUE_WIDTH = 300;

/**
 * 画面サイズ（縦幅）
 */
const VALUE_HEIGHT = 320;

/**
 * データ種別から拠点（Point of Interest）名称のプロパティキーを取得するDictionary
 */
const POI_NAME_KEY_DICT: Record<ODDataType, string> = {
  Bゾーン: "ゾーン名称",
  市区町村: "市区町村名称",
  都道府県: "都道府県名称",
};

/**
 * 出力JSONファイル名
 */
const VALUE_JSON_FILE_NAME = "od_atr_yyyyMMdd_HHmmss.geojson";

/**
 * 出力CSVファイル名
 */
const VALUE_CSV_FILE_NAME = "od_atr_yyyyMMdd_HHmmss.csv";

/**
 * OD集中量属性変換クラス
 */
export class ODNodeConcentrationAttributeConvert implements IAttributeConvert {
  _feature: Feature;

  _odDataType: ODDataType;

  _carType: CarType;

  _yearType: ODYearType;

  _title = "";

  /**
   * コンストラクタ
   * @param feature 地物
   * @param odDataType データ種別
   * @param carType 車種
   * @param yearType 年度
   */
  constructor(
    feature: Feature,
    odDataType: ODDataType,
    carType: CarType,
    yearType: ODYearType
  ) {
    this._feature = feature;
    this._odDataType = odDataType;
    this._carType = carType;
    this._yearType = yearType;
    this._title = `OD集中交通量（${odDataType}）`;
  }

  /**
   * 地物タイプ取得
   */
  getFeatureType(): FeatureType {
    return FeatureType.ODArrival;
  }

  /**
   * 画面用フォーマットに変換
   */
  getViewFormatData(): AttributeView {
    const properties = this._feature.getProperties();

    // ラベル用文字列
    // 調査年度
    const yearLabel = properties.年度 === "R03" ? "令和3年度" : properties.年度;
    // 拠点（Point of Interest）名称
    const poiNameKey = POI_NAME_KEY_DICT[this._odDataType];
    const poiNameLabel = properties[poiNameKey];

    // 画面項目定義リスト
    const items = [
      {
        key: "車種",
        value: this._carType,
        isSimple: true,
      },
      {
        key: "調査年度",
        value: yearLabel,
        isSimple: true,
      },
      {
        key: "移動目的",
        value: properties.目的名称,
        isSimple: true,
      },
      {
        key: "ゾーン名",
        value: poiNameLabel,
        isSimple: true,
      },
      {
        key: "集中量",
        value: properties.交通量,
        isSimple: true,
      },
    ];
    // 画面項目リストでAttributeValue配列を生成しクラス変数に格納後、return
    const attributeValues: AttributeValue[] = [];
    items.forEach((item) => {
      if (item.key == items[4].key) {
        attributeValues.push(
          new AttributeValue(
            item.key + "（トリップ/日）",
            item.value ? Number(item.value).toLocaleString() : null,
            item.isSimple
          )
        );
      } else {
        attributeValues.push(
          new AttributeValue(item.key, item.value.toString(), item.isSimple)
        );
      }
    });
    const attributeView: AttributeView = {
      width: VALUE_WIDTH,
      height: VALUE_HEIGHT,
      maxheight: VALUE_HEIGHT,
      title: this._title,
      attributeValues,
    };
    return attributeView;
  }

  /**
   * ファイル出力用項目取得
   */
  getExportValue(): ExportValue {
    const feature = this._feature.clone();
    feature.getGeometry().transform("EPSG:3857", "EPSG:4326");
    const attributeValues = this.getViewFormatData().attributeValues;
    return new ExportValue(
      feature,
      attributeValues,
      VALUE_JSON_FILE_NAME,
      VALUE_CSV_FILE_NAME
    );
  }
}
